import { LogicalOperator } from "./explanation.dto";
import { SourceRule } from "./sourceRule.dto";

// ============================================================================
// Base types from policyqa_lib.answering.data_model
// ============================================================================

export type TruthValue = "True" | "False" | "DontKnow";

// ============================================================================
// Types from product_rec_lib.trees.condition_node
// ============================================================================

export type LeafNodeValue = "FALSE" | "TRUE" | "UNKNOWN" | "UNDECIDED";

export interface ConditionNodeBase {
  name: string;
  question: string | null;
  logicalOperator: LogicalOperator | null;
  sourceRules: SourceRule[];
  /**
   * If set, this node should only be considered by downstream logic when
   * its value is in this list (e.g. exclusion criteria that only matter when FALSE).
   */
  propagateWhen?: LeafNodeValue[] | null;
}

export interface LeafNode extends ConditionNodeBase {
  value: LeafNodeValue;
  /**
   * If true, this node is not surfaced as a user-facing question,
   * but can still be evaluated (e.g. eligibility checks).
   */
  isInternallyEvaluated?: boolean;
}

export interface InternalNode extends ConditionNodeBase {
  subConditions: Array<InternalNode | LeafNode>;
}

export type ConditionNode = InternalNode | LeafNode;

// ============================================================================
// Types from disclosure_lib.pdf_to_md.reference
// ============================================================================

export interface Reference {
  rawText: string;
  globalReference: string;
  localReference: string;
}

export type ContentType =
  | "NOT_APPLICABLE"
  | "RULE"
  | "DEFINITION"
  | "EXAMPLE"
  | "GUIDANCE"
  | "CONTEXT";

// ============================================================================
// Types from regulation_ingestion.stages.annotation.chalk
// ============================================================================

export interface BoundingRect {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  width: number;
  height: number;
}

export interface Highlight {
  id: string;
  groupId?: string | null;
  text: string;
  boundingRect: BoundingRect;
  rects: BoundingRect[];
  pageNumber: number;
}

// ============================================================================
// Types from policyqa_lib.model.leaf_section
// ============================================================================

export interface LeafSection {
  title: string;
  text: string;
  reference: Reference;
  parentReferences: Reference[];
  contentType: ContentType;
  contentTypeReasoning: string;
  topics: string[];
  externalReferences: any[];
  sourceFileName: string;
  sourceParentTitles: string[];
  highlights: Highlight[];
}

// ============================================================================
// Types from regulation_ingestion.entities.classified_markdown_section
// ============================================================================

export interface ClassifiedMarkdownSection {
  level: number;
  title: string;
  reference: Reference;
  filename: string;
  text: string;
  parentTitles: string[];
  parentReferences: Reference[];
  contentType: ContentType;
  externalReferences: any[];
  contentTypeReasoning: string;
  subsections: ClassifiedMarkdownSection[];
  highlights: Highlight[] | null;
}

// ============================================================================
// Types from policyqa_lib.model.decision_process
// ============================================================================

export interface DecisionProcess {
  type: "DecisionProcess";
  name: string;
  description: string;
  source: LeafSection;
  targetTopic: string | null;
  contentType: ContentType;
}

export interface DecisionTree extends Omit<DecisionProcess, "type"> {
  type: "DecisionTree";
  references: Record<string, Reference>;
  rootNode: ConditionNode | InternalNode | LeafNode;
}

export interface SimpleEvaluationResult {
  evaluationResult: TruthValue;
  scenario: string;
  assignment: Record<string, TruthValue>;
  preEvaluated: Record<string, TruthValue>;
}

export interface EvaluatedDecisionTree extends Omit<DecisionTree, "type"> {
  type: "EvaluatedDecisionTree";
  evaluationResult: TruthValue;
  context: string;
  assignment: Record<string, TruthValue>;
  preEvaluated: Record<string, TruthValue>;
  explanations: Record<string, string>;
}

export interface FormalisedText {
  text: string;
  tree: DecisionTree;
}

export interface StructuredRule extends Omit<DecisionProcess, "type"> {
  type: "StructuredRule";
  triggerCondition: FormalisedText;
  obligations: FormalisedText;
}

export interface EvaluatedStructuredRule extends Omit<StructuredRule, "type"> {
  type: "EvaluatedStructuredRule";
  evaluatedTrigger: EvaluatedDecisionTree;
}

export interface ExampleClassifier extends Omit<DecisionProcess, "type"> {
  type: "ExampleClassifier";
}

// Discriminated union type for all decision process types
export type DecisionProcessTypes =
  | DecisionProcess
  | DecisionTree
  | StructuredRule
  | ExampleClassifier
  | EvaluatedDecisionTree
  | EvaluatedStructuredRule;

export type PaneTypes =
  | TextPane
  | DefinitionPane
  | RulePane
  | ProhibitionPane
  | CompiledRulePane;

export type ReviewStatus = "PENDING" | "APPROVED" | "REJECTED";

export type ProcessedClause = {
  id: string;
  title: string;
  reference: Reference;
  highlights: Highlight[];
  panes: PaneTypes[];
  recordId?: string;
  reviewStatus?: ReviewStatus;
};

export enum PaneType {
  NOT_APPLICABLE = "NOT_APPLICABLE",
  RULE = "RULE",
  DEFINITION = "DEFINITION",
  EXAMPLE = "EXAMPLE",
  GUIDANCE = "GUIDANCE",
  CONTEXT = "CONTEXT",
  PROHIBITION = "PROHIBITION",
  COMPILED_RULE = "COMPILED_RULE",
}
export interface TextPane {
  id: string;
  paneType: PaneType;
  explanation: string;
  excerpt: string;
}

export const isRulePane = (pane: PaneTypes): pane is RulePane => {
  return pane.paneType === PaneType.RULE;
};

export const isDefinitionPane = (pane: PaneTypes): pane is DefinitionPane => {
  return pane.paneType === PaneType.DEFINITION;
};

export interface DefinitionPane extends TextPane {
  definition: DecisionTree;
}

export interface RulePane extends TextPane {
  rule: EvaluatedStructuredRule;
}

export interface ProhibitionPane extends TextPane {
  prohibition: Prohibition;
}

export interface CompiledRulePane extends TextPane {
  compiledRule: RuntimeRule;
}

export interface Prohibition {
  staticSourceExcerpt: string;
  targetTopic: string;
  reasoning: string;
  negatedDefinition: DecisionTree | null;
}

export interface RuntimeRule {
  source: LeafSection;
  triggerText: string;
  obligationText: string;
  triggerEvaluationResult: EvaluatedDecisionTree;
  isCompliantNode: ConditionNode | InternalNode | LeafNode;
}

// Type guard functions for runtime type checking
export function isDecisionProcess(
  obj: DecisionProcessTypes
): obj is DecisionProcess {
  return obj.type === "DecisionProcess";
}

export function isDecisionTree(obj: DecisionProcessTypes): obj is DecisionTree {
  return obj.type === "DecisionTree";
}

export function isEvaluatedDecisionTree(
  obj: DecisionProcessTypes
): obj is EvaluatedDecisionTree {
  return obj.type === "EvaluatedDecisionTree";
}

export function isStructuredRule(
  obj: DecisionProcessTypes
): obj is StructuredRule {
  return obj.type === "StructuredRule";
}

export function isEvaluatedStructuredRule(
  obj: DecisionProcessTypes
): obj is EvaluatedStructuredRule {
  return obj.type === "EvaluatedStructuredRule";
}

export function isExampleClassifier(
  obj: DecisionProcessTypes
): obj is ExampleClassifier {
  return obj.type === "ExampleClassifier";
}

// ============================================================================
// Type guards for ConditionNode variants
// ============================================================================

export function isLeafNode(node: ConditionNode): node is LeafNode {
  return "value" in node;
}

export function isInternalNode(node: ConditionNode): node is InternalNode {
  return "subConditions" in node;
}

export interface GetDecisionProcessesResponse {
  data: DecisionProcessTypes[];
}

export interface GetDecisionProcessResponse {
  data: DecisionProcessTypes;
}
